import React from 'react'
import { Box, Flex, Text } from '@chakra-ui/core'
import { Lock } from 'lucide-react'

import useLocaleText from 'components/useLocaleText'

export interface INoAccessProps {
  /** Locale key to look up the title. Falls back to defaultText. */
  literalKey?: string
  /** Fallback title shown when literalKey is missing or not resolved. */
  defaultText?: string
  /** Locale key to look up the subtitle. Falls back to defaultDescription. */
  descriptionKey?: string
  /** Fallback subtitle shown when descriptionKey is missing or not resolved. */
  defaultDescription?: string
  /** Hide the subtitle line entirely. */
  hideDescription?: boolean
  /** Override the default icon (Lock). Pass null to hide the badge entirely. */
  icon?: React.ReactNode
  /** Icon size in px, inside the circular badge. Default: 32. */
  iconSize?: number
  /** Extra vertical padding around the whole block. Default: '64px'. */
  py?: string
}

const DEFAULT_TEXT = 'No permission to view this page'
const DEFAULT_DESCRIPTION = 'Contact your administrator to request access.'

// Shared "insufficient permission" state — distinct from EmptyState (no results) in icon and intent. Use it
// when a page/section is reachable but the caller lacks the permission to see its content: render this
// instead of the real content and skip any data fetching, rather than hiding the nav entry or redirecting.
export const NoAccess: React.FC<INoAccessProps> = ({
  literalKey = 'no_permission',
  defaultText = DEFAULT_TEXT,
  descriptionKey = 'no_permission_description',
  defaultDescription = DEFAULT_DESCRIPTION,
  hideDescription = false,
  icon,
  iconSize = 32,
  py = '64px',
}) => {
  const localisedText = useLocaleText(literalKey)
  const title = literalKey && localisedText ? localisedText : defaultText

  const localisedDescription = useLocaleText(descriptionKey)
  const description = descriptionKey && localisedDescription ? localisedDescription : defaultDescription

  const renderedIcon =
    icon === null ? null : icon !== undefined ? icon : <Lock size={iconSize} strokeWidth={2} color="#94A3B8" />

  return (
    <Flex direction="column" align="center" justify="center" py={py} px="24px" style={{ gap: '20px' }}>
      {renderedIcon && (
        <Flex
          align="center"
          justify="center"
          w="96px"
          h="96px"
          borderRadius="50%"
          bg="white"
          boxShadow="0 10px 28px rgba(15,23,42,0.10)"
          flexShrink={0}
        >
          {renderedIcon}
        </Flex>
      )}
      <Box textAlign="center" maxW="380px">
        <Text fontSize="18px" fontWeight={700} color="#0f172a" lineHeight="1.4">
          {title}
        </Text>
        {!hideDescription && description && (
          <Text fontSize="14px" fontWeight={400} color="#94a3b8" lineHeight="1.5" mt="8px">
            {description}
          </Text>
        )}
      </Box>
    </Flex>
  )
}
